import { useCallback, useEffect, useState } from 'react';
import type {
  PaigeJobMobile,
  PaigeJobMobileChange,
  PaigeMobileAdded,
  PaigeMobileChannel,
} from './mobile-types';

export interface JobMobileState {
  /** Undefined while it is read; null when the workbench is not on a Paige job. */
  mobile: PaigeJobMobile | null | undefined;
  error: string | null;
  saving: boolean;
  save: (change: PaigeJobMobileChange) => Promise<void>;
  add: (people: { email: string; name?: string }[]) => Promise<PaigeMobileAdded | null>;
  remove: (senderId: string) => Promise<void>;
  resend: (senderId: string) => Promise<void>;
}

/** The job's mobile sending through the bridge: read once, read again after every change. */
export function useJobMobile(): JobMobileState {
  const channel = window.paigeWorkbench?.mobile;
  const [mobile, setMobile] = useState<PaigeJobMobile | null | undefined>(
    channel ? undefined : null,
  );
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (!channel) return;
    let live = true;
    channel
      .read()
      .then((found) => live && setMobile(found))
      .catch((problem: unknown) => live && setError(messageOf(problem)));
    return () => {
      live = false;
    };
  }, [channel]);
  const run = useRun(channel, setMobile, setError, setSaving);
  return { mobile, error, saving, ...changes(channel, run) };
}

type Run = <T>(work: (channel: PaigeMobileChannel) => Promise<T>) => Promise<T | null>;

/** One change, then the sheet read again; a refusal is kept in Paige's words. */
function useRun(
  channel: PaigeMobileChannel | undefined,
  setMobile: (mobile: PaigeJobMobile) => void,
  setError: (error: string | null) => void,
  setSaving: (saving: boolean) => void,
): Run {
  return useCallback(
    async (work) => {
      if (!channel) return null;
      setSaving(true);
      try {
        const result = await work(channel);
        setMobile(await channel.read());
        setError(null);
        return result;
      } catch (problem) {
        setError(messageOf(problem));
        return null;
      } finally {
        setSaving(false);
      }
    },
    [channel, setMobile, setError, setSaving],
  ) as Run;
}

function changes(
  channel: PaigeMobileChannel | undefined,
  run: Run,
): Pick<JobMobileState, 'save' | 'add' | 'remove' | 'resend'> {
  return {
    save: async (change) => void (await run((c) => c.save(change))),
    add: async (people) => (channel ? run((c) => c.add(people)) : null),
    remove: async (senderId) => void (await run((c) => c.remove(senderId))),
    resend: async (senderId) => void (await run((c) => c.resend(senderId))),
  };
}

function messageOf(problem: unknown): string {
  return problem instanceof Error ? problem.message : String(problem);
}
